import type { PageSize, Template } from '../../model/types';
import type { Rect } from './geometry';

export const PAPER = '#fdfdfb';
const LINE = '#c9d3e3';
const MARGIN_LINE = '#ecc3c3';

/** Template spacing in page units (1 unit = 0.21 mm). */
export const RULE_SPACING = 36; // 7.56 mm, college ruled-ish
export const GRID_SPACING = 24; // ≈ 5 mm
const RULE_TOP = 120;
const RULE_MARGIN_X = 120;

/**
 * Draws paper + template for the part of a page inside `r` (page units).
 * The context transform must already map page units to pixels; `pxPerUnit`
 * keeps hairlines about one device pixel wide.
 */
export function drawPaper(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, template: Template, size: PageSize, r: Rect, pxPerUnit: number): void {
  ctx.fillStyle = PAPER;
  ctx.fillRect(r.minX, r.minY, r.maxX - r.minX, r.maxY - r.minY);
  const hair = Math.max(1, pxPerUnit * 0.9) / pxPerUnit;

  switch (template) {
    case 'blank':
      return;
    case 'ruled': {
      ctx.fillStyle = LINE;
      const first = Math.max(RULE_TOP, Math.ceil((r.minY - RULE_TOP) / RULE_SPACING) * RULE_SPACING + RULE_TOP);
      for (let y = first; y <= Math.min(r.maxY, size.height - 40); y += RULE_SPACING) {
        ctx.fillRect(r.minX, y - hair / 2, r.maxX - r.minX, hair);
      }
      if (r.minX <= RULE_MARGIN_X && r.maxX >= RULE_MARGIN_X) {
        ctx.fillStyle = MARGIN_LINE;
        ctx.fillRect(RULE_MARGIN_X - hair / 2, r.minY, hair, r.maxY - r.minY);
      }
      return;
    }
    case 'grid': {
      ctx.fillStyle = LINE;
      for (let y = Math.ceil(r.minY / GRID_SPACING) * GRID_SPACING; y <= r.maxY; y += GRID_SPACING) {
        ctx.fillRect(r.minX, y - hair / 2, r.maxX - r.minX, hair);
      }
      for (let x = Math.ceil(r.minX / GRID_SPACING) * GRID_SPACING; x <= r.maxX; x += GRID_SPACING) {
        ctx.fillRect(x - hair / 2, r.minY, hair, r.maxY - r.minY);
      }
      return;
    }
    case 'dotted': {
      ctx.fillStyle = '#aab4c4';
      const d = Math.max(1.6 / pxPerUnit, 1.2);
      for (let y = Math.ceil(r.minY / GRID_SPACING) * GRID_SPACING; y <= r.maxY; y += GRID_SPACING) {
        for (let x = Math.ceil(r.minX / GRID_SPACING) * GRID_SPACING; x <= r.maxX; x += GRID_SPACING) {
          ctx.fillRect(x - d / 2, y - d / 2, d, d);
        }
      }
      return;
    }
  }
}
